import React, { useState } from 'react';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import { PHONE_NUMBER, PHONE_NUMBER_DISPLAY, trackPhoneClick } from '../utils/tracking';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const Contact = ({ t, language, showForm = true, asPage = false }) => {
  const Heading = asPage ? 'h1' : 'h2';
  const isRo = language === 'ro';
  const [form, setForm] = useState({ name: '', email: '', phone: '', message: '' });
  const [status, setStatus] = useState(null);
  const [loading, setLoading] = useState(false);

  const handlePhoneClick = () => {
    trackPhoneClick();
    window.location.href = `tel:${PHONE_NUMBER}`;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email || !form.message) {
      setStatus('error');
      return;
    }
    setLoading(true);
    try {
      const res = await fetch(`${BACKEND_URL}/api/contact`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(form),
      });
      if (res.ok) {
        setStatus('success');
        setForm({ name: '', email: '', phone: '', message: '' });
        // GTM: Track contact form conversion
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({ event: 'contact_form_submitted' });
      } else {
        setStatus('error');
      }
    } catch {
      setStatus('error');
    }
    setLoading(false);
  };

  const contactItems = [
    { icon: MapPin, label: t.contact.address, value: 'Str. Avram Iancu nr. 56, ap. 2, Brașov', isLink: false },
    { icon: Phone, label: t.contact.phone, value: PHONE_NUMBER_DISPLAY, isLink: true, onClick: handlePhoneClick },
    { icon: Mail, label: t.contact.email, value: 'contact@montreon.ro', isLink: true, href: 'mailto:contact@montreon.ro' },
    { icon: Clock, label: t.contact.hours, value: isRo ? 'Disponibilitate 24/7' : '24/7 Availability', isLink: false }
  ];

  return (
    <section id="contact" className="py-20 sm:py-28 bg-[#ECFDF5]">
      <div className="container mx-auto px-4 sm:px-6 lg:px-12">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16 max-w-3xl mx-auto">
          <p className="text-[#10B981] text-sm font-semibold tracking-[0.15em] uppercase mb-3">
            {isRo ? 'Contactează-ne' : 'Get in Touch'}
          </p>
          <Heading className="font-display text-3xl sm:text-4xl lg:text-5xl font-bold text-[#0D1B3E] leading-tight mb-4" data-testid="contact-page-title">
            {isRo ? 'Pregătit pentru următoarea ta călătorie?' : 'Ready for your next journey?'}
          </Heading>
          <p className="font-body text-base text-gray-500 leading-relaxed">
            {isRo
              ? 'Fie că ai nevoie de o mașină de închiriat, un transfer privat sau un tur turistic, echipa Montréon este pregătită să îți ofere o experiență sigură, confortabilă și fără compromisuri.'
              : 'Whether you need a rental car, a private transfer, or a touristic tour, the Montréon team is ready to offer you a safe, comfortable, and uncompromising experience.'}
          </p>
        </div>

        {/* Contact Form + Info Grid */}
        <div className={`${showForm ? 'grid grid-cols-1 lg:grid-cols-5 gap-8 lg:gap-12' : 'flex flex-col items-center'} max-w-6xl mx-auto`}>
          {/* Contact Form - Homepage only */}
          {showForm && (
          <div className="lg:col-span-3">
            <form onSubmit={handleSubmit} className="bg-white rounded-xl p-6 sm:p-8 shadow-sm border border-gray-100" data-testid="contact-form">
              <h3 className="font-display text-lg font-bold text-[#0D1B3E] mb-6">
                {isRo ? 'Trimite-ne un mesaj' : 'Send us a message'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">
                    {isRo ? 'Nume complet' : 'Full name'} *
                  </label>
                  <input
                    type="text"
                    value={form.name}
                    onChange={e => setForm(f => ({ ...f, name: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm text-[#0D1B3E] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981] transition-colors"
                    placeholder={isRo ? 'Ion Popescu' : 'John Doe'}
                    data-testid="contact-form-name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-600 mb-1.5">Email *</label>
                  <input
                    type="email"
                    value={form.email}
                    onChange={e => setForm(f => ({ ...f, email: e.target.value }))}
                    className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm text-[#0D1B3E] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981] transition-colors"
                    placeholder="email@exemplu.ro"
                    data-testid="contact-form-email"
                  />
                </div>
              </div>
              <div className="mb-4">
                <label className="block text-sm font-medium text-gray-600 mb-1.5">
                  {isRo ? 'Telefon' : 'Phone'}
                </label>
                <input
                  type="tel"
                  value={form.phone}
                  onChange={e => setForm(f => ({ ...f, phone: e.target.value }))}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm text-[#0D1B3E] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981] transition-colors"
                  placeholder="+40 7XX XXX XXX"
                  data-testid="contact-form-phone"
                />
              </div>
              <div className="mb-6">
                <label className="block text-sm font-medium text-gray-600 mb-1.5">
                  {isRo ? 'Mesaj' : 'Message'} *
                </label>
                <textarea
                  value={form.message}
                  onChange={e => setForm(f => ({ ...f, message: e.target.value }))}
                  rows={4}
                  className="w-full px-4 py-3 rounded-lg border border-gray-200 text-sm text-[#0D1B3E] placeholder-gray-400 focus:outline-none focus:ring-2 focus:ring-[#10B981]/30 focus:border-[#10B981] transition-colors resize-none"
                  placeholder={isRo ? 'Scrie mesajul tău aici...' : 'Write your message here...'}
                  data-testid="contact-form-message"
                />
              </div>

              {status === 'success' && (
                <div className="mb-4 p-3 bg-[#ECFDF5] border border-[#10B981]/20 rounded-lg text-sm text-[#10B981] font-medium" data-testid="contact-form-success">
                  {isRo ? 'Mesajul a fost trimis cu succes! Te vom contacta în curând.' : 'Message sent successfully! We will contact you soon.'}
                </div>
              )}
              {status === 'error' && (
                <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600 font-medium" data-testid="contact-form-error">
                  {isRo ? 'Te rugăm să completezi toate câmpurile obligatorii.' : 'Please fill in all required fields.'}
                </div>
              )}

              <div className="flex justify-center">
                <button
                  type="submit"
                  disabled={loading}
                  data-testid="contact-form-submit"
                  className="inline-flex items-center justify-center gap-2 bg-[#10B981] hover:bg-[#0EA472] text-white font-semibold px-10 py-3 rounded-lg shadow-sm hover:shadow-md transition-all text-sm disabled:opacity-60"
                >
                  <Send size={16} />
                  {loading
                    ? (isRo ? 'Se trimite...' : 'Sending...')
                    : (isRo ? 'Trimite Mesajul' : 'Send Message')}
                </button>
              </div>
            </form>
          </div>
          )}

          {/* Contact Cards */}
          <div className={`${showForm ? 'lg:col-span-2' : 'w-full max-w-2xl'} flex flex-col gap-4`}>
            {contactItems.map((item, index) => {
              const Icon = item.icon;
              return (
                <div
                  key={index}
                  data-testid={`contact-item-${index + 1}`}
                  className="bg-white rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow border border-gray-100"
                >
                  <div className="flex items-center gap-4">
                    <div className="w-11 h-11 bg-[#10B981]/10 rounded-full flex items-center justify-center flex-shrink-0">
                      <Icon className="text-[#10B981]" size={20} />
                    </div>
                    <div>
                      <h3 className="font-body text-xs font-semibold text-gray-400 tracking-wider uppercase mb-0.5">
                        {item.label}
                      </h3>
                      {item.isLink ? (
                        item.onClick ? (
                          <button
                            onClick={item.onClick}
                            data-testid="contact-phone-link"
                            className="font-body text-[#0D1B3E] text-base font-semibold hover:text-[#10B981] transition-colors text-left"
                          >
                            {item.value}
                          </button>
                        ) : (
                          <a
                            href={item.href}
                            className="font-body text-[#0D1B3E] text-base font-semibold hover:text-[#10B981] transition-colors"
                          >
                            {item.value}
                          </a>
                        )
                      ) : (
                        <p className="font-body text-[#0D1B3E] text-base font-semibold">
                          {item.value}
                        </p>
                      )}
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* Review Buttons - Centered */}
        <div className="mt-14 flex flex-col sm:flex-row items-center justify-center gap-4" data-testid="contact-trustpilot">
          <a
            href="https://share.google/qWAGU45LXCCn0dlhA"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-review-google-btn"
            className="inline-flex items-center gap-2.5 bg-white text-[#0D1B3E] font-medium px-6 py-3 rounded shadow-sm hover:shadow-md transition-all text-sm"
          >
            <span>Review us on</span>
            <svg className="w-[60px] h-5 flex-shrink-0" viewBox="0 0 272 92">
              <path fill="#4285F4" d="M115.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18C71.25 34.32 81.24 25 93.5 25s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44S80.99 39.2 80.99 47.18c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/>
              <path fill="#EA4335" d="M163.75 47.18c0 12.77-9.99 22.18-22.25 22.18s-22.25-9.41-22.25-22.18c0-12.85 9.99-22.18 22.25-22.18s22.25 9.32 22.25 22.18zm-9.74 0c0-7.98-5.79-13.44-12.51-13.44s-12.51 5.46-12.51 13.44c0 7.9 5.79 13.44 12.51 13.44s12.51-5.55 12.51-13.44z"/>
              <path fill="#FBBC05" d="M209.75 26.34v39.82c0 16.38-9.66 23.07-21.08 23.07-10.75 0-17.22-7.19-19.66-13.07l8.48-3.53c1.51 3.61 5.21 7.87 11.17 7.87 7.31 0 11.84-4.51 11.84-13v-3.19h-.34c-2.18 2.69-6.38 5.04-11.68 5.04-11.09 0-21.25-9.66-21.25-22.09 0-12.52 10.16-22.26 21.25-22.26 5.29 0 9.49 2.35 11.68 4.96h.34v-3.61h9.25zm-8.56 20.92c0-7.81-5.21-13.52-11.84-13.52-6.72 0-12.35 5.71-12.35 13.52 0 7.73 5.63 13.36 12.35 13.36 6.63 0 11.84-5.63 11.84-13.36z"/>
              <path fill="#4285F4" d="M225 3v65h-9.5V3h9.5z"/>
              <path fill="#34A853" d="M262.02 54.48l7.56 5.04c-2.44 3.61-8.32 9.83-18.48 9.83-12.6 0-22.01-9.74-22.01-22.18 0-13.19 9.49-22.18 20.92-22.18 11.51 0 17.14 9.16 18.98 14.11l1.01 2.52-29.65 12.28c2.27 4.45 5.8 6.72 10.75 6.72 4.96 0 8.4-2.44 10.92-6.14zm-23.27-7.98l19.82-8.23c-1.09-2.77-4.37-4.7-8.23-4.7-4.95 0-11.84 4.37-11.59 12.93z"/>
              <path fill="#EA4335" d="M35.29 41.19V32H67c.31 1.64.47 3.58.47 5.68 0 7.06-1.93 15.79-8.15 22.01-6.05 6.3-13.78 9.66-24.02 9.66C16.32 69.35.36 53.89.36 34.91.36 15.93 16.32.47 35.3.47c10.5 0 17.98 4.12 23.6 9.49l-6.64 6.64c-4.03-3.78-9.49-6.72-16.97-6.72-13.86 0-24.7 11.17-24.7 25.03 0 13.86 10.84 25.03 24.7 25.03 8.99 0 14.11-3.61 17.39-6.89 2.66-2.66 4.41-6.46 5.1-11.65l-22.49-.01z"/>
            </svg>
          </a>
          <a
            href="https://www.trustpilot.com/review/montreon.ro"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="contact-review-trustpilot-btn"
            className="inline-flex items-center gap-2.5 bg-white text-[#0D1B3E] font-medium px-6 py-3 rounded shadow-sm hover:shadow-md transition-all text-sm"
          >
            <span>Review us on</span>
            <span className="inline-flex items-center gap-1">
              <svg className="w-5 h-5 flex-shrink-0" viewBox="0 0 40 40" fill="none">
                <rect width="40" height="40" rx="4" fill="#00B67A"/>
                <path d="M20 8l3.5 10.8H35l-9.3 6.7 3.5 10.8L20 29.5l-9.2 6.8 3.5-10.8L5 18.8h11.5L20 8z" fill="#fff"/>
                <path d="M27.2 29.3l-1.4-4.3L20 29.5l7.2-0.2z" fill="#005128"/>
              </svg>
              <span className="font-bold text-[#191919] text-sm">Trustpilot</span>
            </span>
          </a>
        </div>
      </div>
    </section>
  );
};

export default Contact;
